// Anschrift und Branche einer verknüpften Firma — reine ANZEIGE.
//
// Lag bis zuletzt modul-privat in `redesign/ArbeitgeberFeld.tsx`. Seit die
// Kontakt-Anlage denselben Arbeitgeber-Block führt wie die Akte, brauchen beide
// dieselben zwei Zeilen unter dem Firmennamen — und sie müssen dieselbe
// Schreibweise haben, sonst sieht dieselbe Firma in Anlage und Akte anders aus.
//
// Die Werte werden NICHT kopiert und NICHT gespeichert: Sie stehen in der
// Firmenakte und werden dort gepflegt. Zöge der Arbeitgeber um, müsste man sie
// sonst an jedem Mitarbeiter nachziehen.

import * as React from "react";
import { apiUrl } from "../../utils/api";
import { useBrancheGruppen, type BrancheGruppe } from "../../data/brancheGruppen";
import { hauptbranchePfad } from "../forms/widgets/brancheGruppenLogik";

export interface FirmenAnzeige {
  anschrift: string;
  branche: string;
}

/** Die Rohwerte der Firma, aus denen sich die Branche ergibt. */
interface BranchenRohwerte {
  branchengruppeId?: number | null;
  brancheFreitext?: string | null;
  jobIndustry?: string | null;
}

/** „Aachen, Lagerhausstraße 12" — Ort zuerst, wie im Zielbild des PO. */
export function anschriftText(adresse: Record<string, unknown> | null | undefined): string {
  if (!adresse) return "";
  const ort = String(adresse.city ?? "").trim();
  const strasse = String(adresse.street ?? "").trim();
  return [ort, strasse].filter(Boolean).join(", ");
}

/**
 * Die Branche einer Firma als Klartext — aus BEIDEN Katalogen.
 *
 * Bis 09/2026 las diese Anzeige nur `jobIndustry`. Das ist der Anzeigepfad des
 * dreistufigen ALTkatalogs, der beim Speichern als Text mitgeschrieben wird.
 * Wer eine Firma über den heutigen Zwei-Ebenen-Katalog pflegt, füllt dagegen
 * `branchengruppeId` (+ `brancheFreitext`) — und für den stand hier nichts,
 * obwohl die Branche sauber erfasst war.
 *
 * Reihenfolge: der heutige Katalog gewinnt. Steht an einer Firma beides, ist
 * der Altwert der ältere Stand; er bleibt die Rückfallebene für den Bestand,
 * der noch nicht überführt ist.
 *
 * `rows` ist beim ersten Rendern leer (der Katalog kommt über die API). Dann
 * greift der Rückfall, und sobald der Katalog da ist, wird neu gerechnet —
 * ein kurzer Wechsel ist besser als eine dauerhaft leere Zeile.
 */
export function brancheText(
  rows: BrancheGruppe[],
  firma: BranchenRohwerte | null | undefined,
): string {
  if (!firma) return "";
  const gruppeId = Number(firma.branchengruppeId ?? 0);
  if (Number.isFinite(gruppeId) && gruppeId > 0) {
    // Dieselbe Schreibweise wie in der Firmenakte („Branche / Gruppe
    // (Freitext)") — sonst sähe dieselbe Firma hier anders aus als dort.
    const pfad = hauptbranchePfad(rows, [
      { branchengruppeId: gruppeId, freitext: firma.brancheFreitext ?? null },
    ]);
    if (pfad) return pfad;
  }
  return String(firma.jobIndustry ?? "").trim();
}

/**
 * Die Zeilen unter dem Firmennamen — für BEIDE Masken dieselben.
 *
 * Als Textzeilen und nicht als fertiges Bauteil, weil Akte und Anlage in
 * verschiedenen Designsystemen stehen (kvp-rd bzw. Fluent). So bleibt die
 * Schreibweise an einer Stelle, ohne dass sich eine Maske den Stil der anderen
 * einhandelt.
 *
 * Beschriftet, weil „Aachen, Rottstraße 19" allein nicht sagt, WESSEN Anschrift
 * das ist — und eine Branche wie „Immobilienwirtschaft / Sonstige" ohne Label
 * erst recht nicht einzuordnen ist.
 *
 * Steht in der Firmenakte nichts, sagt die Anzeige das ausdrücklich. Genau
 * dieser Fall tritt ein, wenn der Arbeitgeber hier gerade NEU angelegt wurde:
 * Dann ist die Firmenakte noch leer, und eine wortlos fehlende Zeile sähe aus
 * wie ein Fehler der Maske.
 */
export function firmenZusatzZeilen(anzeige: FirmenAnzeige | null | undefined): string[] {
  if (!anzeige) return [];
  const zeilen: string[] = [];
  if (anzeige.anschrift) zeilen.push(`Anschrift: ${anzeige.anschrift}`);
  if (anzeige.branche) zeilen.push(`Branche: ${anzeige.branche}`);
  if (zeilen.length === 0) {
    return ["In der Firmenakte sind Anschrift und Branche noch nicht hinterlegt."];
  }
  // Der Zusatz steht EINMAL unter beiden Zeilen, nicht an jeder: Er sagt, wo
  // gepflegt wird, damit niemand die Werte hier sucht, wenn die Firma umzieht.
  zeilen.push("aus der Firmenakte — dort werden sie gepflegt");
  return zeilen;
}

/**
 * Lädt Anschrift und Branche der verknüpften Firma.
 *
 * Bewusst ein eigener Abruf statt zusätzlicher Relationen am Kontakt-Endpoint:
 * Die Adressen ALLER verbundenen Firmen mitzuladen kostet jede Kundenakte
 * Nutzlast, gebraucht wird aber genau eine. Der Abruf läuft durch dieselbe
 * Rechteprüfung wie jeder Kontakt — fehlt der Zugriff, bleiben die Zeilen
 * einfach weg.
 *
 * Der Branchenkatalog kommt aus `useBrancheGruppen` und ist modulweit gecacht:
 * Er wird einmal je Sitzung geladen, nicht je Arbeitgeber.
 */
export function useFirmenAnzeige(firmaId: number | undefined): FirmenAnzeige | null {
  const [roh, setRoh] = React.useState<
    ({ anschrift: string } & BranchenRohwerte) | null
  >(null);
  const { rows } = useBrancheGruppen();

  React.useEffect(() => {
    setRoh(null);
    if (!firmaId) return;
    const ctrl = new AbortController();
    fetch(apiUrl(`contact/${firmaId}`, { include: "" }), { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((firma) => {
        if (!firma) return;
        setRoh({
          anschrift: anschriftText(firma.defaultAddress),
          branchengruppeId: firma.branchengruppeId ?? null,
          brancheFreitext: firma.brancheFreitext ?? null,
          jobIndustry: firma.jobIndustry ?? null,
        });
      })
      .catch(() => {
        /* Abbruch oder fehlender Zugriff: die Zusatzzeilen entfallen still. */
      });
    return () => ctrl.abort();
  }, [firmaId]);

  // Die Branche wird beim Rendern aufgelöst, nicht beim Laden: Der Katalog
  // trifft oft später ein als die Firma, und ein in den Zustand geschriebener
  // Text bliebe dann für immer leer.
  return React.useMemo(
    () => (roh ? { anschrift: roh.anschrift, branche: brancheText(rows, roh) } : null),
    [roh, rows],
  );
}
